import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { isRegisteredAccountType } from '../presentation/isRegisteredAccountType';

export const RUN_TOP_TICKER_CAP = 3;

export type RunTopTickerRow = {
  ticker: string;
  amountCents: number;
};

export type RunTopTickerRollups = {
  topTaxableSells: RunTopTickerRow[];
  topShelteredSells: RunTopTickerRow[];
  topBuys: RunTopTickerRow[];
  topSells: RunTopTickerRow[];
};

const emptyRollups = (): RunTopTickerRollups => ({
  topTaxableSells: [],
  topShelteredSells: [],
  topBuys: [],
  topSells: [],
});

const addCents = (totals: Map<string, number>, id: string, cents: number) => {
  if (cents <= 0) {
    return;
  }

  totals.set(id, (totals.get(id) ?? 0) + cents);
};

const resolveTickerLabel = (
  financialProductId: string,
  tickerMap: Record<string, string>,
): string | null => {
  const ticker = tickerMap[financialProductId]?.trim();
  return ticker || null;
};

const topResolvedRows = (
  totals: Map<string, number>,
  tickerMap: Record<string, string>,
  cap: number,
): RunTopTickerRow[] =>
  [...totals.entries()]
    .map(([financialProductId, amountCents]) => ({
      ticker: resolveTickerLabel(financialProductId, tickerMap),
      amountCents,
    }))
    .filter((entry): entry is RunTopTickerRow => entry.ticker != null)
    .sort((left, right) => right.amountCents - left.amountCents)
    .slice(0, cap);

/** AI context uses integer cents — org-service prompt bans dollar echo in output. */
export const formatRunTopTickerMoveForAi = (row: RunTopTickerRow): string =>
  `${row.ticker} · ${row.amountCents}`;

export const buildRunTopTickerMovesForAi = (
  rollups: RunTopTickerRollups,
): { largestSells: string[]; largestBuys: string[] } => ({
  largestSells: rollups.topSells.map(formatRunTopTickerMoveForAi),
  largestBuys: rollups.topBuys.map(formatRunTopTickerMoveForAi),
});

export const buildRunTopTickerRollups = (
  audits: OptimizerAuditV2Record[],
  tickerMap: Record<string, string> = {},
): RunTopTickerRollups => {
  if (audits.length === 0) {
    return emptyRollups();
  }

  const taxableSellCentsByProduct = new Map<string, number>();
  const shelteredSellCentsByProduct = new Map<string, number>();
  const buyCentsByProduct = new Map<string, number>();
  const sellCentsByProduct = new Map<string, number>();

  for (const audit of audits) {
    if (audit.passed === false) {
      continue;
    }

    for (const subAccount of audit.subAccountResults ?? []) {
      const registered = isRegisteredAccountType(subAccount.accountType);

      for (const diff of subAccount.weightDiffs ?? []) {
        const financialProductId = diff.financialProductId ?? '';
        if (!financialProductId) {
          continue;
        }

        const deltaCents = diff.deltaCents ?? 0;
        if (deltaCents < 0) {
          const sellCents = Math.abs(deltaCents);
          addCents(sellCentsByProduct, financialProductId, sellCents);
          if (registered) {
            addCents(
              shelteredSellCentsByProduct,
              financialProductId,
              sellCents,
            );
          } else {
            addCents(taxableSellCentsByProduct, financialProductId, sellCents);
          }
        } else if (deltaCents > 0) {
          addCents(buyCentsByProduct, financialProductId, deltaCents);
        }
      }
    }
  }

  return {
    topTaxableSells: topResolvedRows(
      taxableSellCentsByProduct,
      tickerMap,
      RUN_TOP_TICKER_CAP,
    ),
    topShelteredSells: topResolvedRows(
      shelteredSellCentsByProduct,
      tickerMap,
      RUN_TOP_TICKER_CAP,
    ),
    topBuys: topResolvedRows(buyCentsByProduct, tickerMap, RUN_TOP_TICKER_CAP),
    topSells: topResolvedRows(
      sellCentsByProduct,
      tickerMap,
      RUN_TOP_TICKER_CAP,
    ),
  };
};
